import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Flame } from 'lucide-react';
import { getColors, fonts } from '../theme';

/** Full-screen celebration for legendary streaks (100+ days). */
const LegendaryStreakCelebration = ({ streak, displayName, isDark = true, onComplete }) => {
  const colors = getColors(isDark);
  const [visible, setVisible] = useState(true);
  const [fading, setFading] = useState(false);
  const [showText, setShowText] = useState(false);

  const handleDismiss = () => {
    if (fading) return;
    setFading(true);
    setTimeout(() => {
      setVisible(false);
      onComplete?.();
    }, 300);
  };

  useEffect(() => {
    setTimeout(() => setShowText(true), 150);

    const dismissTimer = setTimeout(handleDismiss, 8000);
    return () => clearTimeout(dismissTimer);
  }, []);

  if (!visible) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-300 ${
        fading ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleDismiss}
      style={{ backgroundColor: isDark ? 'rgba(10, 10, 10, 0.96)' : 'rgba(255, 255, 255, 0.96)' }}
    >
      <svg
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none animate-spin-slow"
        width="320"
        height="320"
        viewBox="0 0 320 320"
        aria-hidden="true"
      >
        <circle cx="160" cy="160" r="150" fill="none" stroke={colors.accent} strokeOpacity="0.5" strokeWidth="1" strokeDasharray="2 10" />
      </svg>

      <div
        className={`relative text-center transition-all duration-300 ${
          showText ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        <span
          className="block text-7xl sm:text-8xl tabular-nums animate-scale-in-bounce"
          style={{ fontFamily: fonts.serif, color: colors.accent }}
        >
          {streak}
        </span>

        <p
          className="text-xl sm:text-2xl mt-2 animate-fade-in-up"
          style={{ fontFamily: fonts.serif, color: colors.textPrimary, animationDelay: '0.3s' }}
        >
          day streak
        </p>

        <div
          className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-md animate-fade-in"
          style={{ border: `1px solid ${colors.border}`, animationDelay: '0.6s' }}
        >
          <Flame size={16} strokeWidth={1.75} style={{ color: colors.warning }} />
          <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>
            Legendary
          </span>
        </div>

        <p
          className="mt-6 text-base animate-fade-in"
          style={{ color: colors.textSecondary, animationDelay: '1s' }}
        >
          You should be proud of yourself{displayName ? `, ${displayName}` : ''}
        </p>

        <p
          className="mt-4 text-xs animate-fade-in"
          style={{ color: colors.textMuted, animationDelay: '2s' }}
        >
          Tap anywhere to continue
        </p>
      </div>
    </div>,
    document.body
  );
};

export default LegendaryStreakCelebration;
